import { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, MessageCircle, X, Send } from "lucide-react";

const COUNTRY_CODES = [
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+62", label: "🇮🇩 +62" },
  { code: "+66", label: "🇹🇭 +66" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+84", label: "🇻🇳 +84" },
  { code: "+855", label: "🇰🇭 +855" },
  { code: "+856", label: "🇱🇦 +856" },
  { code: "+95", label: "🇲🇲 +95" },
  { code: "+673", label: "🇧🇳 +673" },
  { code: "+852", label: "🇭🇰 +852" },
  { code: "+853", label: "🇲🇴 +853" },
  { code: "+886", label: "🇹🇼 +886" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+64", label: "🇳🇿 +64" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+1", label: "🇺🇸 +1" },
];
import { toast } from "sonner";
import { firm } from "@/lib/content";
import { submitEnquiry } from "@/lib/api/public.functions";

export function FloatingActions() {
  const [showTop, setShowTop] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!chatOpen) return;
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setChatOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [chatOpen]);

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const detailsVisible = message.trim().length > 0;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = (data.get("name") as string)?.trim();
    const email = (data.get("email") as string)?.trim();
    const enquiryMessage = (data.get("message") as string)?.trim();
    if (!name || !email || !enquiryMessage) return;

    const phoneCode = String(data.get("phoneCode") ?? "");
    const phoneNumber = String(data.get("phone") ?? "").trim();
    const phone = phoneNumber ? `${phoneCode} ${phoneNumber}`.trim() : undefined;

    setSubmitting(true);
    try {
      await submitEnquiry({
        data: {
          name,
          email,
          company: String(data.get("company") ?? "").trim() || undefined,
          phone,
          designation: String(data.get("designation") ?? "").trim() || undefined,
          message: enquiryMessage,
          source: "floating",
        },
      });
      toast.success("Message received", {
        description: `Thanks ${name}. Our team will respond shortly to your enquiry.`,
      });
      form.reset();
      setMessage("");
      setChatOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div ref={popRef} className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 md:bottom-8 md:right-8">
      {chatOpen && (
        <div className="w-[min(92vw,22rem)] overflow-hidden rounded-md border border-border bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-border bg-white px-5 pt-5 pb-3">
            <div>
              <p className="text-base font-bold text-primary">Send us a message</p>
              <p className="mt-0.5 text-sm text-muted-foreground">We typically reply within one business day.</p>
            </div>
            <button
              type="button"
              onClick={() => setChatOpen(false)}
              aria-label="Close"
              className="-mr-1 grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-primary"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="space-y-3 p-5">
            <div>
              <label className="text-sm font-semibold text-primary" htmlFor="fa-message">How can we help?</label>
              <textarea
                id="fa-message"
                name="message"
                required
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your question…"
                className="mt-1 w-full resize-none rounded-lg border border-border px-3 py-2 text-base focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
              />
            </div>

            {detailsVisible && (
              <>
                <FAField id="fa-name" name="name" label="Preferred Name" required />
                <FAField id="fa-email" name="email" type="email" label="Email" required />
                <FAField id="fa-company" name="company" label="Company" required />
                <FAPhoneField />
                <FAField id="fa-designation" name="designation" label="Designation" />
              </>
            )}

            <button
              type="submit"
              disabled={!detailsVisible || submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send <Send className="h-3.5 w-3.5" />
                </>
              )}
            </button>
            <p className="text-[10px] leading-relaxed text-muted-foreground">
              Or email us directly at <a href={`mailto:${firm.email}`} className="font-semibold text-primary hover:underline">{firm.email}</a>
            </p>
          </form>
        </div>
      )}

      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          className="grid h-11 w-11 place-items-center rounded-full border border-border bg-white text-primary shadow-lg transition hover:bg-primary hover:text-primary-foreground"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}

      <button
        type="button"
        onClick={() => setChatOpen((v) => !v)}
        aria-label={chatOpen ? "Close chat" : "Open chat"}
        className="group grid h-13 w-13 place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_15px_35px_-10px_rgb(32_56_112/0.55)] transition hover:bg-primary/90"
        style={{ height: 52, width: 52 }}
      >
        {chatOpen ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>
    </div>
  );
}

function FAField({
  id,
  name,
  label,
  type = "text",
  required,
}: {
  id: string;
  name: string;
  label: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="text-sm font-semibold text-primary" htmlFor={id}>{label}</label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-base focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
      />
    </div>
  );
}

function FAPhoneField() {
  const [code, setCode] = useState("+60");
  return (
    <div>
      <label className="text-sm font-semibold text-primary" htmlFor="fa-phone">Phone</label>
      <div className="mt-1 flex gap-2">
        <select
          name="phoneCode"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="rounded-lg border border-border bg-white px-2 py-2 text-base focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <input
          id="fa-phone"
          name="phone"
          type="tel"
          required
          placeholder="12 345 6789"
          className="w-full rounded-lg border border-border px-3 py-2 text-base focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
        />
      </div>
    </div>
  );
}
